import classNames from 'classnames';
import { useRouter } from 'next/router';
import React from 'react';
import { useDispatch, useSelector } from 'react-redux';

import Heading from '@jpp/atoms/Heading';
import Icons from '@jpp/atoms/Icon';
import { InputField } from '@jpp/atoms/InputField/InputField';
import Loading from '@jpp/atoms/Loading';
import Button from '@jpp/molecules/Button';
import { IReduxState } from 'common/redux/createStore';
import { calculateCartTotals, getCart } from 'common/redux/rootActions';
import { ICartCoupon } from 'common/redux/wooCommerce/cart/reducer';
import { getCartCoupons } from 'common/redux/wooCommerce/cart/selectors';
import { ECtaTheme, ESize } from 'common/typings/enums';
import { addPromoCode, removePromoCode } from 'common/utils/shared/coupons';
import { getQueryParamFirstValue } from 'common/utils/shared/route';

import './CartPromoCode.scss';

interface ICartPromoCodeProps {
  className?: string;
  showPromoInput?: boolean;
}

const ROOT_CLASSNAME = 'CartPromoCode';

const CartPromoCode: React.FC<ICartPromoCodeProps> = ({
  className,
  showPromoInput = true,
}) => {
  const dispatch = useDispatch();
  const router = useRouter();
  const queryCoupon = getQueryParamFirstValue(
    router.query?.coupon || router.query?.['coupon-code']
  );
  const coupons = useSelector((state: IReduxState) => getCartCoupons(state));
  const [promoValue, setPromoValue] = React.useState<string>(queryCoupon || '');
  const [isLoading, setIsLoading] = React.useState<boolean>(false);
  const [error, setError] = React.useState<string>('');
  const [success, setSuccess] = React.useState<string>('');

  const handlePromoOnClick = async (
    event: React.SyntheticEvent<HTMLButtonElement>
  ) => {
    event.preventDefault();
    if (!promoValue) {
      return;
    }
    setIsLoading(true);
    try {
      const { message } = await addPromoCode(promoValue);
      setPromoValue('');
      setError('');
      await dispatch(calculateCartTotals());
      await dispatch(getCart());
      setSuccess(message);
      setIsLoading(false);
      setSuccess('');
    } catch (e) {
      setSuccess('');
      setIsLoading(false);
      setError('Invalid Promo Code');
    }
  };

  const handlePromoRemove = async (
    event: React.SyntheticEvent<HTMLButtonElement>,
    { coupon }: ICartCoupon
  ) => {
    event.preventDefault();
    if (!coupon) {
      return;
    }
    setIsLoading(true);
    try {
      const { message } = await removePromoCode(coupon);
      setSuccess(message);
      setError('');
      await dispatch(calculateCartTotals());
      await dispatch(getCart());
      setIsLoading(false);
      setSuccess('');
    } catch (e) {
      setSuccess('');
      setIsLoading(false);
      setError('Coupon not removed');
    }
  };

  const handleInputOnChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    event.preventDefault();
    setPromoValue(event.target.value);
  };

  if (!showPromoInput && coupons.length === 0) {
    return null;
  }

  return (
    <aside className={classNames(ROOT_CLASSNAME, className)}>
      {isLoading && (
        <div className={`${ROOT_CLASSNAME}__loading`}>
          <Loading />
        </div>
      )}

      <Heading
        className={classNames(`${ROOT_CLASSNAME}__heading`, {
          [`${ROOT_CLASSNAME}__heading--has-copy`]:
            !!queryCoupon && coupons.length === 0,
        })}
        priority={2}
        children="Promo Code"
      />

      {queryCoupon && coupons.length === 0 && (
        <p className={`${ROOT_CLASSNAME}__copy`}>
          We have detected a coupon, would you like to apply it?
        </p>
      )}

      {showPromoInput && (
        <>
          {coupons?.length === 0 && (
            <div className={`${ROOT_CLASSNAME}__form`}>
              <InputField
                className={`${ROOT_CLASSNAME}__input`}
                name="promo-code"
                defaultValue={promoValue}
                onChange={handleInputOnChange}
                tabIndex={0}
              />

              <Button
                type="button"
                behaviour="action"
                size={ESize.Sm}
                theme={ECtaTheme.BrandOutline}
                onClick={handlePromoOnClick}
                className={`${ROOT_CLASSNAME}__button`}
                tabIndex={0}
              >
                Apply
              </Button>
            </div>
          )}
        </>
      )}

      {coupons?.length > 0 && (
        <>
          <Heading
            priority={3}
            className={`${ROOT_CLASSNAME}__subheading`}
            children={`Applied Coupon${coupons.length > 1 ? 's' : ''}`}
          />
          <ul className={`${ROOT_CLASSNAME}__list`}>
            {coupons.map((_code, index) => {
              const code = Array.isArray(_code) ? _code[1] : _code;

              return (
                <li
                  key={`${code.label} ${index}`}
                  className={`${ROOT_CLASSNAME}__item`}
                >
                  {code.label}

                  <button
                    type="button"
                    className={`${ROOT_CLASSNAME}__item-delete`}
                    onClick={(event) => handlePromoRemove(event, code)}
                  >
                    <Icons.Close className={`${ROOT_CLASSNAME}__item-icon`} />
                  </button>
                </li>
              );
            })}
          </ul>
        </>
      )}

      {error && <p className={`${ROOT_CLASSNAME}__error`}>{error}</p>}

      {success && <p className={`${ROOT_CLASSNAME}__success`}>{success}</p>}
    </aside>
  );
};

export default CartPromoCode;
